import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  IconButton,
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { BodyMeasurementService } from '../services/bodyMeasurementService';
import type {
  BodyMeasurementsByDate,
  BodyMeasurementChartPoint,
  LatestMeasurements,
} from '../services/types';
import { METRIC_TYPES } from '../services/types';

const TOOLTIP_NO_DATE_TEXT = 'Няма данни';

const formatTooltipDate = (value: unknown): string => {
  if (typeof value !== 'string' || value.trim() === '') {
    return TOOLTIP_NO_DATE_TEXT;
  }

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return TOOLTIP_NO_DATE_TEXT;
  }

  const day = `${parsedDate.getDate()}`.padStart(2, '0');
  const month = `${parsedDate.getMonth() + 1}`.padStart(2, '0');
  const year = parsedDate.getFullYear();

  return `${day}.${month}.${year}`;
};

const BodyMeasurements: React.FC = () => {
  // Form state
  const [measuredAt, setMeasuredAt] = useState(new Date().toISOString().split('T')[0]);
  const [formValues, setFormValues] = useState<{ [key: string]: string }>({});
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Chart state
  const [selectedMetric, setSelectedMetric] = useState(METRIC_TYPES[0].key);
  const [chartData, setChartData] = useState<BodyMeasurementChartPoint[]>([]);
  const [chartLoading, setChartLoading] = useState(false);

  // Table state
  const [tableData, setTableData] = useState<BodyMeasurementsByDate[]>([]);
  const [tableLoading, setTableLoading] = useState(false);

  // Latest measurements (for pre-filling form)
  const [latestMeasurements, setLatestMeasurements] = useState<LatestMeasurements>({});

  // Load initial data
  useEffect(() => {
    loadTableData();
    loadLatestMeasurements();
  }, []);

  // Load chart data when metric changes
  useEffect(() => {
    loadChartData(selectedMetric);
  }, [selectedMetric]);

  const loadTableData = async () => {
    setTableLoading(true);
    try {
      const data = await BodyMeasurementService.getMeasurementsByDate(20);
      setTableData(data);
    } catch (error) {
      console.error('Failed to load table data:', error);
    } finally {
      setTableLoading(false);
    }
  };

  const loadChartData = async (metric: string) => {
    setChartLoading(true);
    try {
      const data = await BodyMeasurementService.getChartData(metric);
      setChartData(data);
    } catch (error) {
      console.error('Failed to load chart data:', error);
    } finally {
      setChartLoading(false);
    }
  };

  const loadLatestMeasurements = async () => {
    try {
      const data = await BodyMeasurementService.getLatest();
      setLatestMeasurements(data);
    } catch (error) {
      console.error('Failed to load latest measurements:', error);
    }
  };

  const handleFormChange = (key: string, value: string) => {
    setFormValues(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    // Filter out empty values and convert to numbers
    const measurements: { [key: string]: number } = {};
    for (const [key, value] of Object.entries(formValues)) {
      if (value !== '' && value !== undefined) {
        const num = parseFloat(value);
        if (!isNaN(num)) {
          measurements[key] = num;
        }
      }
    }

    if (Object.keys(measurements).length === 0) {
      setSaveMessage({ type: 'error', text: 'Въведете поне едно измерване' });
      return;
    }

    setSaving(true);
    setSaveMessage(null);

    try {
      await BodyMeasurementService.batchSave({
        measuredAt,
        measurements,
        userId: 1,
      });
      setSaveMessage({ type: 'success', text: 'Измерванията са записани успешно!' });
      setFormValues({});
      // Reload data
      loadTableData();
      loadChartData(selectedMetric);
      loadLatestMeasurements();
    } catch (error) {
      setSaveMessage({ type: 'error', text: 'Грешка при записване на измерванията' });
      console.error('Failed to save measurements:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteByDate = async (date: string) => {
    if (!window.confirm(`Сигурни ли сте, че искате да изтриете всички измервания за ${date}?`)) {
      return;
    }

    try {
      await BodyMeasurementService.deleteByDate(date);
      loadTableData();
      loadChartData(selectedMetric);
      loadLatestMeasurements();
    } catch (error) {
      console.error('Failed to delete measurements:', error);
    }
  };

  const selectedMetricInfo = METRIC_TYPES.find(m => m.key === selectedMetric);
  const chartRenderData = chartData.map((point, index) => ({
    ...point,
    xLabel: point.date && point.date.trim() !== '' ? point.date : `record-${index + 1}`,
  }));

  const renderChartTooltip = ({ active, payload }: { active?: boolean; payload?: any[] }) => {
    if (!active || !payload || payload.length === 0) {
      return null;
    }

    const point = payload[0]?.payload as BodyMeasurementChartPoint | undefined;
    const value = payload[0]?.value;
    const tooltipDate = formatTooltipDate(point?.measured_at ?? point?.date);

    return (
      <Paper sx={{ p: 1 }}>
        <Typography variant="body2">
          Стойност: {value} {selectedMetricInfo?.unit ?? ''}
        </Typography>
        <Typography variant="body2">
          Дата: {tooltipDate}
        </Typography>
      </Paper>
    );
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Телесни Измервания
      </Typography>

      {/* Form Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Ново измерване
        </Typography>

        <Box sx={{ mb: 2 }}>
          <TextField
            label="Дата"
            type="date"
            value={measuredAt}
            onChange={(e) => setMeasuredAt(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 200 }}
          />
        </Box>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          {METRIC_TYPES.map((metric) => (
            <Grid size={{ xs: 6, sm: 4, md: 3 }} key={metric.key}>
              <TextField
                label={`${metric.label} (${metric.unit})`}
                type="number"
                value={formValues[metric.key] || ''}
                onChange={(e) => handleFormChange(metric.key, e.target.value)}
                placeholder={
                  latestMeasurements[metric.key]
                    ? `Последно: ${latestMeasurements[metric.key].value}`
                    : ''
                }
                inputProps={{ step: 0.1, min: 0 }}
                fullWidth
                size="small"
              />
            </Grid>
          ))}
        </Grid>

        {saveMessage && (
          <Alert severity={saveMessage.type} sx={{ mb: 2 }} onClose={() => setSaveMessage(null)}>
            {saveMessage.text}
          </Alert>
        )}

        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? <CircularProgress size={24} /> : 'Запази'}
        </Button>
      </Paper>

      {/* Chart Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Графика
        </Typography>

        <FormControl sx={{ minWidth: 200, mb: 2 }}>
          <InputLabel id="body-measurement-metric-label">Метрика</InputLabel>
          <Select
            labelId="body-measurement-metric-label"
            id="body-measurement-metric-select"
            value={selectedMetric}
            label="Метрика"
            onChange={(e) => setSelectedMetric(e.target.value)}
          >
            {METRIC_TYPES.map((metric) => (
              <MenuItem key={metric.key} value={metric.key}>
                {metric.label} ({metric.unit})
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {chartLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : chartData.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            Няма данни за {selectedMetricInfo?.label}
          </Typography>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartRenderData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="xLabel" />
              <YAxis
                domain={['auto', 'auto']}
                label={{
                  value: selectedMetricInfo?.unit || '',
                  angle: -90,
                  position: 'insideLeft',
                }}
              />
              <Tooltip
                content={renderChartTooltip}
              />
              <Line
                type="monotone"
                dataKey="value"
                stroke="#1976d2"
                strokeWidth={2}
                dot={{ r: 4 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </Paper>

      {/* Table Section */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Последни измервания
        </Typography>

        {tableLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : tableData.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            Няма записани измервания
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Дата</TableCell>
                  {METRIC_TYPES.map((metric) => (
                    <TableCell key={metric.key} align="right">
                      {metric.label}
                      <br />
                      <Typography variant="caption" color="text.secondary">
                        ({metric.unit})
                      </Typography>
                    </TableCell>
                  ))}
                  <TableCell align="center">Действия</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tableData.map((row) => (
                  <TableRow key={row.date}>
                    <TableCell>{row.date}</TableCell>
                    {METRIC_TYPES.map((metric) => (
                      <TableCell key={metric.key} align="right">
                        {row.measurements[metric.key] != null
                          ? row.measurements[metric.key]
                          : '-'}
                      </TableCell>
                    ))}
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteByDate(row.date)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
};

export default BodyMeasurements;
